// strategyValidation.ts — TSL v1.1.1-compliant validator.
//
// COVERAGE:
//   • V-1 through V-14    — core structural validators (from TSL v1.0/v1.1)
//   • V-4c                — PriceData.shift integer requirement (TSL v1.1.1 A.8)
//   • V-6f                — inside_range/outside_range structured-operand pairing (TSL v1.1.1 A.7)
//   • V-22a/b/c           — StopLoss breakeven primary vs overlay disambiguation (TSL v1.1.1 A.3)
//   • V-26b/c             — Exit/SL/TP target resolution + scale-out cumulative cap (TSL v1.1.1 B.6)
//   • W-1 through W-13    — performance / best-practice warnings (TSL v1.0/v1.1)
//
// CODES DEFINED BY TSL v1.1.1 BUT NOT YET ENFORCED HERE (runtime-only or
// require deeper engine work — documented for completeness):
//   • V-24a/b   — Port type compatibility (graph.ts wiring check; runtime-detected today via NaN propagation)
//   • V-25      — Take Profit risk_reward can't resolve a Stop Loss (engine-side, halts at runtime)
//   • V-26a     — Exit/SL/TP target unresolvable at load (requires Position.entryBlockId schema)
//   • V-27      — Unknown TSL version on load (loader-side; handled by tsl_version envelope check)
//   • W-19      — Deprecated MomentumOscillator flat fields (migration warning; engine accepts both shapes)
//   • W-20      — Auto-migrated 'sar' from MovingAverageBlock to TrendBlock (migration warning)
//   • W-21      — position_sizing_method vs default_qty_type disagreement (loader-side)
//   • W-22      — Ambiguous risk-reward link in TP resolution (engine-side W when multiple SL candidates)
//
// HARD ERROR CODES (E-*) DEFINED BY TSL v1.1.1 B.7:
//   • E-1  Custom Block 3 consecutive timeouts        (customBlockRuntime emits)
//   • E-2  Custom Block 3 exceptions in 10 bars       (customBlockRuntime emits)
//   • E-3  Custom Block output shape mismatch         (customBlockRuntime emits)
//   • E-4  Graph mutation detected at runtime         (impossible — engine bug guard)
//   • E-5  Broker / data source connection lost       (live engine emits)
//   • E-6  Resource limit exceeded                    (custom block tier ceiling)

import {
  Block,
  BlockType,
  ValidationResult,
  ValidationError,
  ValidationWarning,
  ExpressionSourceType,
  TSL_CONNECTION_RULES,
  isTerminalBlock,
  requiresStructuredFlow,
  effectiveDisabledIds,
} from '../../types';
import { detectWiredPriceExits, validateSizingConfig, detectUnimplementedExits } from '../../engine/strategyGuards';

export interface ValidationRule {
  id: string;
  name: string;
  type: 'critical' | 'error' | 'warning' | 'info';
  category:
    | 'structure'
    | 'logic'
    | 'risk'
    | 'performance'
    | 'best_practice'
    | 'pattern'
    | 'signal_flow'
    | 'price_data'
    | 'order_management'
    | 'position_management'
    | 'custom_block';
  validate: (blocks: Block[]) => ValidationError[];
}

export interface PerformanceMetrics {
  complexity: number;
  estimatedMemoryUsage: number;
  estimatedExecutionTime: number;
  cycleComplexity: number;
  indicatorLoad: number;
}

class StrategyValidator {
  private rules: ValidationRule[] = [];

  constructor() {
    this.initializeRules();
  }

  // Helper methods
  private isSourceBlock(blockType: BlockType): boolean {
    // In TSL v1.0, indicators have implicit access to global price data
    return blockType === BlockType.PRICE_DATA || 
           [BlockType.MOVING_AVERAGE, BlockType.MOMENTUM_OSCILLATOR, BlockType.VOLATILITY, 
            BlockType.VOLUME, BlockType.TREND, BlockType.CUSTOM,
            BlockType.CHART_PATTERNS, BlockType.CANDLESTICK_PATTERNS, BlockType.TIME_FILTER].includes(blockType);
  }

  private isLogicBlock(blockType: BlockType): boolean {
    return [BlockType.EXPRESSION, BlockType.LOGIC_GATE].includes(blockType);
  }

  private markReachable(
    blockId: string,
    blocks: Block[],
    reachable: Set<string>
  ): void {
    if (reachable.has(blockId)) return;

    reachable.add(blockId);
    const block = blocks.find((b) => b.id === blockId);

    if (block?.connections?.outputs) {
      block.connections.outputs.forEach((outputId) => {
        this.markReachable(outputId, blocks, reachable);
      });
    }
  }

  private initializeRules(): void {
    // V-1: Strategy Configuration Requirements (TSL V-1a, V-1b, V-1c)
    this.addRule({
      id: 'strategy_config_requirements',
      name: 'Strategy Configuration Requirements',
      type: 'critical',
      category: 'structure',
      validate: (blocks) => {
        const configBlocks = blocks.filter((b) => {
          try {
            return b && b.type === BlockType.STRATEGY_CONFIG;
          } catch (error) {
            console.warn('Error checking block type:', error, b);
            return false;
          }
        });

        const errors: ValidationError[] = [];

        // V-1a: Exactly one Strategy Configuration block must exist
        if (configBlocks.length === 0) {
          errors.push({
            id: 'missing_strategy_config',
            type: 'critical',
            message:
              'Strategy must have exactly one Strategy Configuration block',
            suggestion:
              'Add a Strategy Configuration block to define basic strategy parameters',
            code: 'V1A_MISSING_STRATEGY_CONFIG',
          });
        } else if (configBlocks.length > 1) {
          errors.push({
            id: 'multiple_strategy_config',
            type: 'critical',
            message: 'Strategy can only have one Strategy Configuration block',
            suggestion: 'Remove duplicate Strategy Configuration blocks',
            code: 'V1A_MULTIPLE_STRATEGY_CONFIG',
          });
        }

        // V-1b & V-1c: Strategy Config cannot have connections
        if (configBlocks.length > 0) {
          const configBlock = configBlocks[0];

          // V-NOSTOP (Phase 2A.1) — explicit canonical no-stop policy is fail-closed.
          const slPolicyRaw = (configBlock.data as Record<string, unknown>).stopLossPolicy;
          if (slPolicyRaw !== undefined && slPolicyRaw !== null && slPolicyRaw !== '') {
            const slPolicy = String(slPolicyRaw).toLowerCase();
            if (slPolicy !== 'none') {
              errors.push({
                id: 'invalid_stop_loss_policy',
                type: 'error',
                message: `Invalid stopLossPolicy "${String(slPolicyRaw)}" — the only supported value is "none".`,
                blockId: configBlock.id,
                suggestion: 'Remove stopLossPolicy, or set it to "none" to declare an explicitly stopless strategy.',
                code: 'V_NOSTOP_INVALID',
              });
            } else {
              // No-stop is incompatible with risk-based sizing (which needs a stop
              // distance) unless an explicit stop block supplies one.
              const method = String(
                (configBlock.data as Record<string, unknown>).position_sizing_method
                ?? (configBlock.data as Record<string, unknown>).default_qty_type ?? '',
              ).toLowerCase();
              const needsStop = /risk/.test(method);
              const hasStopBlock = blocks.some((b) => b && b.type === BlockType.STOP_LOSS);
              if (needsStop && !hasStopBlock) {
                errors.push({
                  id: 'nostop_with_risk_sizing',
                  type: 'error',
                  message: `stopLossPolicy "none" cannot be combined with risk-based sizing ("${method}"), which requires a stop distance.`,
                  blockId: configBlock.id,
                  suggestion: 'Use percent-equity / fixed sizing, or add a stop-loss block, or remove the no-stop declaration.',
                  code: 'V_NOSTOP_RISK_SIZING',
                });
              }
            }
          }

          if (configBlock.connections?.inputs?.length > 0) {
            errors.push({
              id: 'config_has_inputs',
              type: 'critical',
              message:
                'Strategy Configuration block cannot have input connections',
              blockId: configBlock.id,
              suggestion:
                'Remove all input connections from Strategy Configuration block',
              code: 'V1B_CONFIG_HAS_INPUTS',
            });
          }

          if (configBlock.connections?.outputs?.length > 0) {
            errors.push({
              id: 'config_has_outputs',
              type: 'critical',
              message:
                'Strategy Configuration block cannot have output connections',
              blockId: configBlock.id,
              suggestion:
                'Remove all output connections from Strategy Configuration block',
              code: 'V1C_CONFIG_HAS_OUTPUTS',
            });
          }
        }

        return errors;
      },
    });

    // GUARD: price-configured SL/TP block with an indicator wired into it.
    // The engine silently discards the price stop/target and turns the block
    // into an unconditional "close-all when truthy" gate. This exact footgun
    // bled the 1m BTC deployment. Make it loud and actionable.
    this.addRule({
      id: 'wired_price_exit_footgun',
      name: 'Price SL/TP with indicator wired in',
      type: 'error',
      category: 'risk',
      validate: (blocks) => {
        const errors: ValidationError[] = [];
        for (const issue of detectWiredPriceExits(blocks)) {
          errors.push({
            id: issue.code.toLowerCase(),
            type: 'error',
            message: issue.message,
            blockId: issue.blockId,
            suggestion: issue.suggestion,
            code: issue.code,
          });
        }
        for (const issue of validateSizingConfig(blocks)) {
          errors.push({
            id: issue.code.toLowerCase(),
            type: 'warning',
            message: issue.message,
            suggestion: issue.suggestion,
            code: issue.code,
          });
        }
        // Unimplemented exit types: an inert TP / silently-mis-stopped SL must
        // be LOUD, never silent (engine-level ATR support intentionally not
        // added — surfaced here instead).
        for (const issue of detectUnimplementedExits(blocks)) {
          errors.push({
            id: issue.code.toLowerCase(),
            type: 'error',
            message: issue.message,
            blockId: issue.blockId,
            suggestion: issue.suggestion,
            code: issue.code,
          });
        }
        return errors;
      },
    });

    // V-2: Entry Logic Requirements (TSL V-2a, V-2b, V-2c)
    this.addRule({
      id: 'entry_logic_requirements',
      name: 'Entry Logic Requirements',
      type: 'critical',
      category: 'structure',
      validate: (blocks) => {
        const errors: ValidationError[] = [];
        const entryBlocks = blocks.filter((b) => b.type === BlockType.ENTRY);

        // V-2a: At least one Entry block must exist
        if (entryBlocks.length === 0) {
          errors.push({
            id: 'missing_entry_logic',
            type: 'critical',
            message: 'Strategy must have at least one entry condition',
            suggestion: 'Add an Entry block to define when to enter trades',
            code: 'V2A_MISSING_ENTRY_LOGIC',
          });
        }

        // V-2b: Entry blocks must have signal flow path from indicators or patterns through logical blocks
        entryBlocks.forEach((entry) => {
          const hasValidSignalFlow = this.hasValidSignalFlowToEntry(
            entry.id,
            blocks
          );
          if (!hasValidSignalFlow) {
            errors.push({
              id: 'entry_invalid_signal_flow',
              type: 'critical',
              message:
                'Entry block must receive signals through structured flow (indicators/patterns → logic blocks → entry)',
              blockId: entry.id,
              suggestion:
                'Connect indicators or patterns through Expression or Logic Gate blocks to this entry',
              code: 'V2B_ENTRY_INVALID_SIGNAL_FLOW',
            });
          }
        });

        // V-2c: Entry blocks must specify either 'long' or 'short' direction
        entryBlocks.forEach((entry) => {
          const direction = entry.data.direction;
          // Type-safe check - no 'both' handling needed
          if (!direction) {
            errors.push({
              id: 'entry_missing_direction',
              type: 'critical',
              message: 'Entry blocks must specify a direction',
              blockId: entry.id,
              suggestion: 'Set direction to either "long" or "short"',
              code: 'V2C_ENTRY_MISSING_DIRECTION',
            });
          }
          // No need to check for 'both' - TypeScript prevents it
        });

        return errors;
      },
    });

    // V-3: Signal Flow Validation (TSL V-3a, V-3b, V-3c, V-3d)
    this.addRule({
      id: 'structured_signal_flow',
      name: 'Structured Signal Flow Validation',
      type: 'critical',
      category: 'signal_flow',
      validate: (blocks) => {
        const errors: ValidationError[] = [];

        // Check that all indicator outputs flow through logic blocks
        blocks.forEach((block) => {
          if (requiresStructuredFlow(block.type)) {
            // V-3b carve-out: per-trade custom blocks (qty_calculator /
            // sl_calculator / tp_calculator) output a single NUMBER consumed
            // by entry sizing or SL/TP resolution — NOT a per-bar signal.
            // They legitimately connect directly to ENTRY blocks; forcing
            // them through Expression/Logic Gate would be a category error.
            // Identify by data.blockType so this stays a property of the
            // block's role, not of its top-level BlockType.
            const bt = (block.data as Record<string, unknown> | undefined)?.blockType;
            // Slice B.1 — risk_rule joins the per-trade-custom carve-out.
            // Like qty/sl/tp calculators, risk_rule blocks emit per-trade
            // actions consumed by the engine's entry / bar / trade-close
            // hooks, not per-bar signal flow.
            const isPerTradeCustom =
              block.type === BlockType.CUSTOM
              && (bt === 'qty_calculator' || bt === 'sl_calculator' || bt === 'tp_calculator' || bt === 'risk_rule');
            if (isPerTradeCustom) return;
            block.connections.outputs.forEach((outputId) => {
              const targetBlock = blocks.find((b) => b.id === outputId);
              if (targetBlock && !this.isLogicBlock(targetBlock.type)) {
                errors.push({
                  id: 'invalid_indicator_flow',
                  type: 'critical',
                  message: `${this.getBlockTypeName(block.type)} must connect to Expression or Logic Gate blocks`,
                  blockId: block.id,
                  suggestion: `Connect ${this.getBlockTypeName(block.type)} to an Expression or Logic Gate block`,
                  code: 'V3B_INVALID_INDICATOR_FLOW',
                });
              }
            });
          }
        });

        return errors;
      },
    });

    // V-4: Price Data Connection Constraints (TSL V-4a, V-4b)
    this.addRule({
      id: 'price_data_connection_constraints',
      name: 'Price Data Connection Constraints',
      type: 'error',
      category: 'price_data',
      validate: (blocks) => {
        const errors: ValidationError[] = [];

        // V-4a: No more than one PRICE_DATA block can connect to any single destination block
        const priceDataBlocks = blocks.filter((b) => b.type === BlockType.PRICE_DATA);
        const destinationBlocks = new Map<string, string[]>();

        priceDataBlocks.forEach((priceBlock) => {
          priceBlock.connections.outputs.forEach((outputId) => {
            if (!destinationBlocks.has(outputId)) {
              destinationBlocks.set(outputId, []);
            }
            destinationBlocks.get(outputId)!.push(priceBlock.id);
          });
        });

        destinationBlocks.forEach((sourceIds, targetId) => {
          if (sourceIds.length > 1) {
            errors.push({
              id: 'multiple_price_data_connections',
              type: 'error',
              message: 'Multiple Price Data blocks cannot connect to the same destination block',
              blockId: targetId,
              suggestion: 'Connect only one Price Data block to each destination block',
              code: 'V4A_MULTIPLE_PRICE_DATA_CONNECTIONS',
            });
          }
        });

        // V-4c (TSL v1.1.1 A.8): PriceData.shift MUST be an integer number of
        // bars at the block's own timeframe. Non-integer is ambiguous —
        // implementations could round differently. Reject at validation time.
        priceDataBlocks.forEach((priceBlock) => {
          const raw = priceBlock.data.shift;
          if (raw === undefined || raw === null) return;
          const n = Number(raw);
          if (!Number.isFinite(n) || !Number.isInteger(n)) {
            errors.push({
              id: `${priceBlock.id}-shift-non-integer`,
              type: 'error',
              code: 'V4C_PRICE_DATA_SHIFT_NON_INTEGER',
              message: `V-4c: PriceData.shift must be an integer number of bars (got ${raw}).`,
              blockId: priceBlock.id,
              field: 'shift',
              suggestion: 'shift counts bars at THIS block\'s timeframe; e.g. shift=-1 reads the previous bar.',
            });
          }
        });

        return errors;
      },
    });

    // V-5: Terminal Block Validation (TSL V-5a, V-5b, V-5c, V-5d, V-5e)
    this.addRule({
      id: 'terminal_block_validation',
      name: 'Terminal Block Validation',
      type: 'critical',
      category: 'structure',
      validate: (blocks) => {
        const errors: ValidationError[] = [];

        // V-5a: Terminal blocks cannot have output connections
        blocks.forEach((block) => {
          if (isTerminalBlock(block.type) && block.connections.outputs.length > 0) {
            errors.push({
              id: 'terminal_block_has_outputs',
              type: 'critical',
              message: `${this.getBlockTypeName(block.type)} blocks cannot have output connections`,
              blockId: block.id,
              suggestion: 'Remove all output connections from this terminal block',
              code: 'V5A_TERMINAL_BLOCK_HAS_OUTPUTS',
            });
          }
        });

        // V-5b: Entry and Exit blocks must have at least one input connection
        blocks.forEach((block) => {
          if ([BlockType.ENTRY, BlockType.EXIT].includes(block.type) && 
              block.connections.inputs.length === 0) {
            errors.push({
              id: 'entry_exit_no_inputs',
              type: 'critical',
              message: `${this.getBlockTypeName(block.type)} blocks must have at least one input connection`,
              blockId: block.id,
              suggestion: 'Connect this block to a logic block (Expression or Logic Gate)',
              code: 'V5B_ENTRY_EXIT_NO_INPUTS',
            });
          }
        });

        // V-5c: Stop Loss and Take Profit blocks can have zero or one input connection
        blocks.forEach((block) => {
          if ([BlockType.STOP_LOSS, BlockType.TAKE_PROFIT].includes(block.type) && 
              block.connections.inputs.length > 1) {
            errors.push({
              id: 'risk_block_multiple_inputs',
              type: 'critical',
              message: `${this.getBlockTypeName(block.type)} blocks can have at most one input connection`,
              blockId: block.id,
              suggestion: 'Connect only one logic block to this risk management block',
              code: 'V5C_RISK_BLOCK_MULTIPLE_INPUTS',
            });
          }
        });

        // V-5d: When Stop Loss or Take Profit blocks have input connections, they must be from Expression or Logic Gate blocks
        blocks.forEach((block) => {
          if ([BlockType.STOP_LOSS, BlockType.TAKE_PROFIT].includes(block.type) &&
              block.connections.inputs.length > 0) {
            block.connections.inputs.forEach((inputId) => {
              const inputBlock = blocks.find((b) => b.id === inputId);
              if (inputBlock && !this.isLogicBlock(inputBlock.type)) {
                errors.push({
                  id: 'risk_block_invalid_input',
                  type: 'critical',
                  message: `${this.getBlockTypeName(block.type)} blocks can only receive inputs from Expression or Logic Gate blocks`,
                  blockId: block.id,
                  suggestion: 'Connect this risk management block only to Expression or Logic Gate blocks',
                  code: 'V5D_RISK_BLOCK_INVALID_INPUT',
                });
              }
            });
          }
        });

        // S11a (2026-06-03) — F54 fix. LOGIC_GATE with < 2 inputs
        // is degenerate. AND of one input = that input; NAND just
        // negates. Functionally a no-op pass-through. Engine
        // doesn't crash but the gate adds no value — likely a
        // strategy authoring mistake. Warn (not critical) so the
        // user can ignore intentionally if they want a placeholder
        // gate for later.
        blocks.forEach((block) => {
          if (block.type === BlockType.LOGIC_GATE && block.connections.inputs.length < 2) {
            errors.push({
              id: 'logic_gate_too_few_inputs',
              type: 'warning',
              message: `${this.getBlockTypeName(block.type)} has fewer than 2 inputs — it's a no-op pass-through`,
              blockId: block.id,
              suggestion: 'Connect at least 2 upstream Expression or LogicGate blocks. To negate a single signal, use the "Invert result" checkbox on the producing Expression instead.',
              code: 'S11A_LOGIC_GATE_TOO_FEW_INPUTS',
            });
          }
        });

        // S11a — F58 fix. TP with tpType='risk_reward' but no
        // STOP_LOSS in the strategy silently fails (engine returns
        // undefined when stopDistance can't be resolved). Catch it
        // pre-run.
        const hasAnyStopLoss = blocks.some((b) => b.type === BlockType.STOP_LOSS);
        blocks.forEach((block) => {
          if (block.type === BlockType.TAKE_PROFIT &&
              (block.data as Record<string, unknown>).tpType === 'risk_reward' &&
              !hasAnyStopLoss) {
            errors.push({
              id: 'tp_rr_without_sl',
              type: 'critical',
              message: 'Take-Profit with risk_reward type needs at least one Stop-Loss block',
              blockId: block.id,
              suggestion: 'Add a Stop-Loss block so the engine can compute the risk distance. R/R math is undefined without it.',
              code: 'S11A_TP_RR_WITHOUT_SL',
            });
          }
        });

        // S11a — F57 fix. Expression with non-static source but no
        // resolution target silently produces NaN. Surface as a
        // warning so the user catches the orphan wiring.
        blocks.forEach((block) => {
          if (block.type !== BlockType.EXPRESSION) return;
          const data = block.data as Record<string, unknown>;
          for (const side of ['left', 'right'] as const) {
            const source = data[`${side}Source`] as string | undefined;
            if (source === 'input' && !data[`${side}InputBlockId`]) {
              errors.push({
                id: `expr_${side}_input_unresolved`,
                type: 'warning',
                message: `Expression's ${side} operand source is "input" but no block is selected`,
                blockId: block.id,
                suggestion: `Draw an edge from an upstream block to this Expression (auto-fills ${side}InputBlockId) OR pick a block from the ${side}-operand dropdown.`,
                code: `S11A_EXPR_${side.toUpperCase()}_INPUT_UNRESOLVED`,
              });
            }
            if (source === 'price_data' && !data[`${side}PriceDataBlockId`]) {
              errors.push({
                id: `expr_${side}_pricedata_unresolved`,
                type: 'warning',
                message: `Expression's ${side} operand source is "price_data" but no Price Data block is selected`,
                blockId: block.id,
                suggestion: 'Draw an edge from a Price Data block to this Expression, or pick one from the dropdown.',
                code: `S11A_EXPR_${side.toUpperCase()}_PRICEDATA_UNRESOLVED`,
              });
            }
          }
        });

        // S11a — F56 fix. Entry's *CalculatorBlockId must resolve
        // to a CUSTOM block with the matching subtype. Stale
        // pointers silently fall through to the legacy method-
        // switch sizing (calling itself "the override is active"
        // in the UI while not actually being).
        const expectedSubtype: Record<string, string> = {
          qtyCalculatorBlockId: 'qty_calculator',
          slCalculatorBlockId: 'sl_calculator',
          tpCalculatorBlockId: 'tp_calculator',
        };
        blocks.forEach((block) => {
          if (block.type !== BlockType.ENTRY) return;
          const data = block.data as Record<string, unknown>;
          for (const [field, requiredSubtype] of Object.entries(expectedSubtype)) {
            const ptr = data[field];
            if (typeof ptr !== 'string' || ptr.length === 0) continue;
            const target = blocks.find((b) => b.id === ptr);
            if (!target) {
              errors.push({
                id: `entry_calc_pointer_stale_${field}`,
                type: 'critical',
                message: `Entry's ${field} points at a block (${ptr.slice(-8)}) that no longer exists`,
                blockId: block.id,
                suggestion: `Clear the ${field} field or re-wire to an existing CUSTOM block of subtype ${requiredSubtype}.`,
                code: 'S11A_ENTRY_CALC_POINTER_STALE',
              });
              continue;
            }
            const targetSubtype = (target.data as Record<string, unknown> | undefined)?.blockType;
            if (target.type !== BlockType.CUSTOM || targetSubtype !== requiredSubtype) {
              errors.push({
                id: `entry_calc_pointer_wrong_subtype_${field}`,
                type: 'critical',
                message: `Entry's ${field} points at a block that is not a ${requiredSubtype} custom block`,
                blockId: block.id,
                suggestion: `Re-wire ${field} to a CUSTOM block with blockType="${requiredSubtype}", or change the target block's subtype.`,
                code: 'S11A_ENTRY_CALC_POINTER_WRONG_SUBTYPE',
              });
            }
          }
        });

        // TSL migration — silent quarantine is now a VALIDATION ERROR.
        // The engine executes custom blocks only from `data.tslAst` (safe-TSL
        // interpreter) or a recognized `parameters.__canonical` marker
        // (trusted native implementation). Host JavaScript in `data.code` is
        // quarantined at run time: it produces no signals, no sizing, no risk
        // actions — the block is a no-op. Pre-migration that no-op was
        // silent; these checks make every non-executable custom block an
        // explicit, actionable error so no surface can create a strategy
        // whose blocks are silently dropped. (Replaces the S11a host-code
        // contract check, which looked for computeQty/checkBar/… function
        // names inside code the engine no longer runs.)
        blocks.forEach((block) => {
          if (block.type !== BlockType.CUSTOM) return;
          const data = block.data as Record<string, unknown>;
          const subtype = String(data.blockType ?? 'indicator');
          const hasCanonical = typeof (data.parameters as { __canonical?: unknown } | undefined)?.__canonical === 'string';
          const ast = data.tslAst as { risk?: unknown; qty?: unknown } | null | undefined;
          const hasAst = ast != null && typeof ast === 'object';
          const hasCode = String(data.code ?? '').trim().length > 0;
          const canonicalOnly = subtype === 'sl_calculator' || subtype === 'tp_calculator';

          // 1) Legacy host JS with no executable representation → explicit rejection.
          if (hasCode && !hasAst && !hasCanonical) {
            errors.push({
              id: `custom_legacy_js_${block.id}`,
              type: 'critical',
              message: `Custom ${subtype} block contains legacy JavaScript, which is never executed (quarantined) — the block is a silent no-op`,
              blockId: block.id,
              suggestion: 'Open the block and re-author it as safe TSL (the editor shows the legacy source read-only), replace it with a library block, or select a native canonical implementation. Host JavaScript is not compiled or executed anywhere in the engine.',
              code: 'TSL_LEGACY_JS_QUARANTINED',
            });
            return;
          }
          // 2) Canonical-only subtypes (sl/tp) execute ONLY via a marker.
          if (canonicalOnly && !hasCanonical) {
            errors.push({
              id: `custom_canonical_required_${block.id}`,
              type: 'critical',
              message: `${subtype} blocks run only as native canonical implementations — this block has no parameters.__canonical marker${hasAst ? ' (its inline TSL body is not executed for this subtype)' : ''}`,
              blockId: block.id,
              suggestion: `Open the block and select a native implementation (e.g. ${subtype === 'sl_calculator' ? 'ftmo_calcsl_v1' : 'ftmo_calctp_v1'}), or use the ${subtype === 'sl_calculator' ? 'STOP_LOSS' : 'TAKE_PROFIT'} terminal block's built-in modes instead.`,
              code: 'TSL_CANONICAL_MARKER_REQUIRED',
            });
            return;
          }
          // 3) A tslAst must carry the section its subtype dispatches on —
          //    otherwise the engine evaluates nothing (risk_rule reads
          //    tslAst.risk at checkBar; qty_calculator reads tslAst.qty).
          if (!hasCanonical && hasAst && subtype === 'risk_rule' && !ast!.risk) {
            errors.push({
              id: `custom_tsl_missing_risk_${block.id}`,
              type: 'critical',
              message: 'risk_rule block\'s TSL body has no `risk:` section — the engine has nothing to evaluate',
              blockId: block.id,
              suggestion: 'Add a `risk: <action-code expression>` section (0 allow · 1 block_entry · 2 flatten) or select a canonical prop-firm rule via parameters.__canonical.',
              code: 'TSL_MISSING_SECTION',
            });
            return;
          }
          if (!hasCanonical && hasAst && subtype === 'qty_calculator' && !ast!.qty) {
            errors.push({
              id: `custom_tsl_missing_qty_${block.id}`,
              type: 'critical',
              message: 'qty_calculator block\'s TSL body has no `qty:` section — the engine has nothing to evaluate',
              blockId: block.id,
              suggestion: 'Add a `qty: <expression over ctx + params>` section or select a canonical implementation via parameters.__canonical.',
              code: 'TSL_MISSING_SECTION',
            });
            return;
          }
          // 4) No executable representation at all → the block cannot affect
          //    the backtest (per-bar fallback emits zeros/false).
          if (!hasCanonical && !hasAst && !canonicalOnly) {
            errors.push({
              id: `custom_no_executable_${block.id}`,
              type: 'critical',
              message: `Custom ${subtype} block has no executable body (no TSL, no canonical marker) — it contributes nothing to the backtest`,
              blockId: block.id,
              suggestion: 'Open the block and author a safe-TSL body (a starter template is provided), or install a block from the library.',
              code: 'TSL_NOT_EXECUTABLE',
            });
          }
        });

        // S11a — F59 fix. CUSTOM block with empty / whitespace
        // code is a silent no-op (engine returns empty BlockOutput).
        blocks.forEach((block) => {
          if (block.type !== BlockType.CUSTOM) return;
          const d = block.data as Record<string, unknown>;
          // Marker/AST-dispatched blocks run without host code — not an empty-no-op.
          if ((d.parameters as { __canonical?: unknown } | undefined)?.__canonical || d.tslAst) return;
          const code = String(d.code ?? '').trim();
          if (code.length === 0) {
            errors.push({
              id: 'custom_empty_code',
              type: 'warning',
              message: 'Custom block has no code — it will produce no signals at run time',
              blockId: block.id,
              suggestion: 'Open the Custom Block Editor and paste your code, or use the "Use starter template" button.',
              code: 'S11A_CUSTOM_EMPTY_CODE',
            });
          }
        });

        return errors;
      },
    });

    // V-6: Enhanced Expression Validation (TSL V-6a through V-6e)
    this.addRule({
      id: 'expression_validation',
      name: 'Expression Block Validation',
      type: 'error',
      category: 'logic',
      validate: (blocks) => {
        const errors: ValidationError[] = [];
        const expressionBlocks = blocks.filter(
          (b) => b.type === BlockType.EXPRESSION
        );

        expressionBlocks.forEach((block) => {
          errors.push(...this.validateExpressionBlock(block, blocks));
        });

        return errors;
      },
    });

    // V-7: Pattern Recognition Validation (TSL V-7a through V-7d)
    this.addRule({
      id: 'pattern_recognition_validation',
      name: 'Pattern Recognition Validation',
      type: 'error',
      category: 'pattern',
      validate: (blocks) => {
        const errors: ValidationError[] = [];
        const patternBlocks = blocks.filter((b) =>
          [
            BlockType.CHART_PATTERNS,
            BlockType.CANDLESTICK_PATTERNS,
          ].includes(b.type)
        );

        patternBlocks.forEach((block) => {
          errors.push(...this.validatePatternBlock(block));
        });

        return errors;
      },
    });

    // V-8: Position Sizing Validation (TSL V-8a through V-8d)
    this.addRule({
      id: 'position_sizing_validation',
      name: 'Position Sizing Validation',
      type: 'error',
      category: 'risk',
      validate: (blocks) => {
        const errors: ValidationError[] = [];
        const entryBlocks = blocks.filter((b) => b.type === BlockType.ENTRY);

        entryBlocks.forEach((block) => {
          // V-8a: Entry blocks with custom position sizing must have valid parameters.
          // `position_sizing_method` and `quantity_type` are SYNONYMS in the engine —
          // sizing.ts reads `eb.position_sizing_method ?? eb.quantity_type`, and so do
          // graphCompiler's `declaresSizing` and StrategySettings' entry-override check.
          // The validator previously only recognized quantity_type; that lagged behind
          // the canonical field name and made entries written with `position_sizing_method`
          // (e.g. the FTMO Sweep template after Pine-parity refresh) falsely fail V-8a.
          if (!block.data.useStrategyDefaults) {
            // V-8a accepts ANY of three sizing declarations:
            //   • position_sizing_method (canonical method-name field)
            //   • quantity_type           (older synonym; still in saved data)
            //   • qtyCalculatorBlockId    (TSL v1.2-pre §X — entry delegates
            //                              sizing to a qty_calculator custom
            //                              block whose code computes qty)
            // The engine resolves all three (qty_calculator wins, then method
            // fields, then config fallback). Validator must mirror.
            if (
              !block.data.quantity_type
              && !block.data.position_sizing_method
              && !block.data.qtyCalculatorBlockId
            ) {
              errors.push({
                id: 'entry_missing_quantity_type',
                type: 'error',
                message: 'Entry block with custom position sizing must specify quantity type or a qty_calculator block',
                blockId: block.id,
                suggestion: 'Set quantity_type / position_sizing_method, reference a qty_calculator via qtyCalculatorBlockId, or use strategy defaults',
                code: 'V8A_MISSING_QUANTITY_TYPE',
              });
            }

            // V-8b: Kelly Criterion sizing requires valid lookback and fraction parameters
            if (block.data.position_sizing_method === 'kelly_criterion') {
              if (!block.data.kelly_lookback || block.data.kelly_lookback < 10) {
                errors.push({
                  id: 'kelly_invalid_lookback',
                  type: 'error',
                  message: 'Kelly Criterion requires lookback period of at least 10',
                  blockId: block.id,
                  suggestion: 'Set kelly_lookback to at least 10 periods',
                  code: 'V8B_KELLY_INVALID_LOOKBACK',
                });
              }
              if (!block.data.kelly_fraction || block.data.kelly_fraction <= 0 || block.data.kelly_fraction > 1) {
                errors.push({
                  id: 'kelly_invalid_fraction',
                  type: 'error',
                  message: 'Kelly Criterion fraction must be between 0 and 1',
                  blockId: block.id,
                  suggestion: 'Set kelly_fraction between 0 and 1 (recommended: 0.25)',
                  code: 'V8B_KELLY_INVALID_FRACTION',
                });
              }
            }

            // V-8c: Risk-based sizing requires valid risk_per_trade percentage (0-100)
            if (block.data.position_sizing_method === 'risk_based') {
              if (!block.data.risk_per_trade || block.data.risk_per_trade <= 0 || block.data.risk_per_trade > 100) {
                errors.push({
                  id: 'risk_based_invalid_percentage',
                  type: 'error',
                  message: 'Risk-based sizing requires risk_per_trade between 0 and 100',
                  blockId: block.id,
                  suggestion: 'Set risk_per_trade between 0 and 100 (recommended: 1-5%)',
                  code: 'V8C_RISK_BASED_INVALID_PERCENTAGE',
                });
              }
            }

            // V-8d: Min/max position sizes must be logically consistent (min ≤ max)
            if (block.data.min_position_size && block.data.max_position_size) {
              if (block.data.min_position_size > block.data.max_position_size) {
                errors.push({
                  id: 'position_size_inconsistent',
                  type: 'error',
                  message: 'Minimum position size cannot be greater than maximum position size',
                  blockId: block.id,
                  suggestion: 'Ensure min_position_size ≤ max_position_size',
                  code: 'V8D_POSITION_SIZE_INCONSISTENT',
                });
              }
            }
          }
        });

        return errors;
      },
    });

    // V-9: Stop Loss and Take Profit Execution Method Validation (TSL V-9a through V-9f)
    this.addRule({
      id: 'execution_method_validation',
      name: 'Execution Method Validation',
      type: 'error',
      category: 'order_management',
      validate: (blocks) => {
        const errors: ValidationError[] = [];
        const riskBlocks = blocks.filter((b) => 
          [BlockType.STOP_LOSS, BlockType.TAKE_PROFIT].includes(b.type)
        );

        riskBlocks.forEach((block) => {
          // S4c (2026-06-02) — V-9a un-gated from the dead `executionMethod`
          // field. Pre-S4c the entire SL/TP subtype validation was wrapped
          // in `if (executionMethod === 'condition_based')`. The engine
          // dispatched on stopType/tpType DIRECTLY and never read
          // executionMethod, so the gate was an artifact of an unimplemented
          // condition_based vs indicator_based distinction. Now the
          // subtype-required-fields check ALWAYS runs when a subType is
          // present. We dropped the "subType must be set" branch because
          // the engine defaults missing stopType to 'fixed_points'
          // (strategyGuards.ts:67) — flagging it as a hard error would
          // regress strategies that legitimately rely on the default.
          //
          // V-9b (which required an input connection when executionMethod
          // was 'indicator_based') is REMOVED entirely — TSL V-5c says
          // SL/TP with zero inputs are legal (the engine handles them as
          // price-based stops), so V-9b was always wrong-shaped.
          //
          // Subtype → required field map:
          //   stop_loss:   fixed_points / percentage → stopValue
          //                atr_based → atrPeriod AND atrMultiplier
          //                trailing → trailingStep
          //   take_profit: fixed_points / percentage → tpValue
          //                risk_reward → riskRewardRatio
          //                atr_based → atrPeriod AND atrMultiplier
          {
            const typeField = block.type === BlockType.STOP_LOSS ? 'stopType' : 'tpType';
            const subType = block.data[typeField] as string | undefined;
            if (subType) {
              const positive = (v: unknown) =>
                typeof v === 'number' && Number.isFinite(v) && v > 0;
              const pushMissing = (field: string, suggest?: string) => {
                errors.push({
                  id: `condition_based_missing_${field}`,
                  type: 'error',
                  message: `${this.getBlockTypeName(block.type)} with ${typeField}="${subType}" must specify ${field}${suggest ? ' (suggest ' + suggest + ')' : ''}`,
                  blockId: block.id,
                  suggestion: `Set ${field}${suggest ? ' to ' + suggest : ''} for ${subType} execution`,
                  code: 'V9A_CONDITION_BASED_MISSING_PARAMS',
                });
              };

              if (block.type === BlockType.STOP_LOSS) {
                if (subType === 'fixed_points' || subType === 'percentage') {
                  if (!positive(block.data.stopValue)) pushMissing('stopValue');
                } else if (subType === 'atr_based') {
                  if (!positive(block.data.atrPeriod))     pushMissing('atrPeriod', '14');
                  if (!positive(block.data.atrMultiplier)) pushMissing('atrMultiplier', '2');
                } else if (subType === 'trailing' || subType === 'trailing_limit') {
                  // T-1m — trailingType selects which distance field is
                  // required: 'atr' → atrMultiplier, 'percentage' →
                  // trailingPercent, 'points' (default) → trailingStep.
                  const trailingType = block.data.trailingType as string | undefined;
                  if (trailingType === 'atr') {
                    if (!positive(block.data.atrMultiplier)) pushMissing('atrMultiplier', '2');
                  } else if (trailingType === 'percentage') {
                    if (!positive(block.data.trailingPercent)) pushMissing('trailingPercent');
                  } else if (!positive(block.data.trailingStep)) {
                    pushMissing('trailingStep');
                  }
                }
              } else { // TAKE_PROFIT
                if (subType === 'fixed_points' || subType === 'percentage') {
                  if (!positive(block.data.tpValue)) pushMissing('tpValue');
                } else if (subType === 'risk_reward') {
                  if (!positive(block.data.riskRewardRatio)) pushMissing('riskRewardRatio', '2');
                } else if (subType === 'atr_based') {
                  if (!positive(block.data.atrPeriod))     pushMissing('atrPeriod', '14');
                  if (!positive(block.data.atrMultiplier)) pushMissing('atrMultiplier', '2');
                }
              }
            }
          }

          // V-9c: ATR-based types must have valid atrMultiplier and atrPeriod
          const atrType = block.type === BlockType.STOP_LOSS ? 'atr_based' : 'atr_based';
          if (block.data[block.type === BlockType.STOP_LOSS ? 'stopType' : 'tpType'] === atrType) {
            if (!block.data.atrMultiplier || block.data.atrMultiplier <= 0) {
              errors.push({
                id: 'atr_based_invalid_multiplier',
                type: 'error',
                message: 'ATR-based type must have valid atrMultiplier > 0',
                blockId: block.id,
                suggestion: 'Set atrMultiplier to a positive value',
                code: 'V9C_ATR_BASED_INVALID_PARAMS',
              });
            }
            if (!block.data.atrPeriod || block.data.atrPeriod <= 0) {
              errors.push({
                id: 'atr_based_invalid_period',
                type: 'error',
                message: 'ATR-based type must have valid atrPeriod > 0',
                blockId: block.id,
                suggestion: 'Set atrPeriod to a positive value',
                code: 'V9C_ATR_BASED_INVALID_PARAMS',
              });
            }
          }

          // V-9d: Risk-reward type must have valid riskRewardRatio
          if (block.type === BlockType.TAKE_PROFIT && block.data.tpType === 'risk_reward') {
            if (!block.data.riskRewardRatio || block.data.riskRewardRatio <= 0) {
              errors.push({
                id: 'risk_reward_invalid_ratio',
                type: 'error',
                message: 'Risk-reward type must have valid riskRewardRatio > 0',
                blockId: block.id,
                suggestion: 'Set riskRewardRatio to a positive value',
                code: 'V9D_RISK_REWARD_INVALID_RATIO',
              });
            }
          }

          // S4c (2026-06-02) — V-9e removed (scaled exit-mode validator).
          // The `scaledLevels` field is gone from the BlockData type;
          // the engine never iterated it. `exitMode: 'scaled'` itself
          // remains as a schema token; `detectUnimplementedExits` in
          // src/engine/strategyGuards.ts already surfaces it as
          // unimplemented. See docs/BLOCK_SETTINGS_AUDIT.md S4c section.

          // V-9f: Partial exit mode must have valid partialQuantity (0-100)
          if (block.data.exitMode === 'partial') {
            if (!block.data.partialQuantity || block.data.partialQuantity <= 0 || block.data.partialQuantity > 100) {
              errors.push({
                id: 'partial_exit_invalid_percentage',
                type: 'error',
                message: 'Partial exit mode must have valid partialQuantity between 0-100',
                blockId: block.id,
                suggestion: 'Set partialQuantity to a value between 0 and 100',
                code: 'V9F_PARTIAL_EXIT_INVALID_PERCENTAGE',
              });
            }
          }
        });

        return errors;
      },
    });

    // V-10: Subgraph Terminal Isolation Validation (TSL V-10a through V-10d)
    this.addRule({
      id: 'subgraph_terminal_isolation',
      name: 'Subgraph Terminal Isolation Validation',
      type: 'critical',
      category: 'structure',
      validate: (blocks) => {
        const errors: ValidationError[] = [];
        const subgraphs = this.findConnectedSubgraphs(blocks);

        subgraphs.forEach((subgraph) => {
          const terminalBlocks = subgraph.filter((blockId) => {
            const block = blocks.find((b) => b.id === blockId);
            return block && isTerminalBlock(block.type);
          });

          // V-10a: Each connected subgraph can have at most one terminal block
          if (terminalBlocks.length > 1) {
            errors.push({
              id: 'multiple_terminals_in_subgraph',
              type: 'critical',
              message: `Subgraph contains ${terminalBlocks.length} terminal blocks (maximum allowed: 1)`,
              suggestion: 'Separate terminal blocks into different subgraphs or remove duplicate terminals',
              code: 'V10A_MULTIPLE_TERMINALS_IN_COMPONENT',
            });
          }

          // V-10d: Entry and Exit terminal blocks must be reachable from at least one data source
          terminalBlocks.forEach((terminalId) => {
            const terminal = blocks.find((b) => b.id === terminalId);
            if (terminal && [BlockType.ENTRY, BlockType.EXIT].includes(terminal.type)) {
              const hasDataSource = this.hasDataSource(terminalId, blocks, subgraph);
              if (!hasDataSource) {
                errors.push({
                  id: 'terminal_not_reachable',
                  type: 'critical',
                  message: `${this.getBlockTypeName(terminal.type)} block is not reachable from any data source`,
                  blockId: terminalId,
                  suggestion: 'Connect this terminal block to a data source through logic blocks',
                  code: 'V10D_TERMINAL_NOT_REACHABLE',
                });
              }
            }
          });
        });

        return errors;
      },
    });

    // V-11: Trade Direction Validation (TSL V-11a through V-11c)
    this.addRule({
      id: 'trade_direction_validation',
      name: 'Trade Direction Validation',
      type: 'critical',
      category: 'structure',
      validate: (blocks) => {
        const errors: ValidationError[] = [];
        const entryBlocks = blocks.filter((b) => b.type === BlockType.ENTRY);

        entryBlocks.forEach((entry) => {
          const direction = entry.data.direction;
          
          // V-11a: Entry blocks must specify exactly one direction: either 'long' or 'short'
          if (!direction || !['long', 'short'].includes(direction)) {
            errors.push({
              id: 'entry_invalid_direction',
              type: 'critical',
              message: 'Entry blocks must specify exactly one direction: either "long" or "short"',
              blockId: entry.id,
              suggestion: 'Set direction to either "long" or "short"',
              code: 'V11A_ENTRY_INVALID_DIRECTION',
            });
          }
        });

        return errors;
      },
    });

    // V-12: Order Management Validation (TSL V-12a through V-12e)
    this.addRule({
      id: 'order_management_validation',
      name: 'Order Management Validation',
      type: 'error',
      category: 'order_management',
      validate: (blocks) => {
        const errors: ValidationError[] = [];
        const entryBlocks = blocks.filter((b) => b.type === BlockType.ENTRY);

        entryBlocks.forEach((block) => {
          // V-12a: Entry blocks with bracket orders must have valid stop loss and take profit values
          if (block.data.orderType === 'bracket') {
            if (!block.data.bracketStopLoss || !block.data.bracketTakeProfit) {
              errors.push({
                id: 'bracket_missing_values',
                type: 'error',
                message: 'Bracket orders must have valid stop loss and take profit values',
                blockId: block.id,
                suggestion: 'Set bracketStopLoss and bracketTakeProfit values',
                code: 'V12A_BRACKET_MISSING_VALUES',
              });
            }
          }

          // V-12b: Trailing orders must have valid trailing amount and activation parameters
          if (block.data.orderType === 'trailing') {
            if (!block.data.trailingAmount || block.data.trailingAmount <= 0) {
              errors.push({
                id: 'trailing_invalid_amount',
                type: 'error',
                message: 'Trailing orders must have valid trailing amount > 0',
                blockId: block.id,
                suggestion: 'Set trailingAmount to a positive value',
                code: 'V12B_TRAILING_INVALID_PARAMS',
              });
            }
            if (!block.data.trailingActivation || block.data.trailingActivation <= 0) {
              errors.push({
                id: 'trailing_invalid_activation',
                type: 'error',
                message: 'Trailing orders must have valid activation price offset > 0',
                blockId: block.id,
                suggestion: 'Set trailingActivation to a positive value',
                code: 'V12B_TRAILING_INVALID_PARAMS',
              });
            }
          }

          // S4b (2026-06-02) — V-12c removed (entry scale-in validators).
          //
          // Pre-S4b this block validated `block.data.allowScaling`,
          // `maxScaleIns`, and `scaleInSize`. The engine has never iterated
          // any of these fields — pyramiding is driven by `cfg.pyramiding`
          // on the strategy_config block, not by per-entry "scale-in"
          // settings. The fields were removed from the BlockData type in
          // the same slice; the validator that policed them was dead too.
          // See docs/BLOCK_SETTINGS_AUDIT.md S4b section.
        });

        // S4b (2026-06-02) — V-12d removed (exit scale-out level validators).
        //
        // Pre-S4b this validated `block.data.allowScaleOut` and the
        // per-level `trigger` / `percentage` shape of
        // `block.data.scaleOutLevels[]`. The engine never iterated
        // scaleOutLevels — ladder-exits were never implemented. The
        // companion V-26c (cumulative-sum check) is also gone in this
        // slice. See docs/BLOCK_SETTINGS_AUDIT.md.

        // V-12e: Exit blocks with partial sizing must have valid
        // exit quantity (0-100).
        //
        // S10n (2026-06-03) — F35 fix. Pre-S10n this validator read
        // `block.data.exitSize === 'partial'` and `exitPercentage`
        // — BOTH dead fields with zero engine consumers. The engine
        // reads `exitMode === 'partial'` (an ExitType enum value)
        // and `partialQuantity` (the integer percent). Validator
        // realigned to the live fields so it actually fires when
        // a real partial exit is mis-configured.
        const exitBlocks = blocks.filter((b) => b.type === BlockType.EXIT);
        exitBlocks.forEach((block) => {
          const data = block.data as Record<string, unknown>;
          if (data.exitMode === 'partial') {
            const partialQty = Number(data.partialQuantity);
            if (!Number.isFinite(partialQty) || partialQty <= 0 || partialQty > 100) {
              errors.push({
                id: 'partial_exit_invalid_percentage',
                type: 'error',
                message: 'Partial exit must have a valid partialQuantity between 1 and 100',
                blockId: block.id,
                suggestion: 'Set Partial Quantity (%) to a value between 1 and 100',
                code: 'V12E_PARTIAL_EXIT_INVALID_PERCENTAGE',
              });
            }
          }
        });

        return errors;
      },
    });

    // V-13: Position Management Validation (TSL V-13a through V-13d)
    this.addRule({
      id: 'position_management_validation',
      name: 'Position Management Validation',
      type: 'error',
      category: 'position_management',
      validate: (blocks) => {
        const errors: ValidationError[] = [];

        // V-13a: Stop Loss blocks with breakeven functionality must have valid trigger values.
        // TSL v1.1.1 A.3 (V-22): the breakeven semantics split into two disambiguated modes:
        //   • PRIMARY breakeven (stopType: 'breakeven') uses `breakevenTrigger`
        //   • MIGRATION OVERLAY (moveToBreakeven: true) uses `moveToBreakevenTrigger`
        // The old single `breakevenTrigger` paired with `moveToBreakeven` is back-compat-
        // tolerated (V-22b reads either field) but new strategies should use the new name.
        const stopLossBlocks = blocks.filter((b) => b.type === BlockType.STOP_LOSS);
        stopLossBlocks.forEach((block) => {
          const d = block.data;
          // V-22c: stopType 'breakeven' + moveToBreakeven:true together is AMBIGUOUS
          // — two modes simultaneously. Hard error.
          if (d.stopType === 'breakeven' && d.moveToBreakeven === true) {
            errors.push({
              id: 'breakeven_mode_ambiguous',
              type: 'error',
              message: 'V-22c: cannot use both stopType:"breakeven" and moveToBreakeven:true on the same block. Pick one mode.',
              blockId: block.id,
              suggestion: 'Set stopType to a non-breakeven type (e.g. percentage) and keep moveToBreakeven, OR drop moveToBreakeven.',
              code: 'V22C_BREAKEVEN_AMBIGUOUS',
            });
          }
          // V-22a: stopType 'breakeven' requires breakevenTrigger > 0. moveToBreakevenTrigger is IGNORED.
          if (d.stopType === 'breakeven') {
            const t = Number(d.breakevenTrigger);
            if (!Number.isFinite(t) || t <= 0) {
              errors.push({
                id: 'breakeven_primary_missing_trigger',
                type: 'error',
                message: 'V-22a: stopType:"breakeven" requires breakevenTrigger > 0.',
                blockId: block.id,
                suggestion: 'Set breakevenTrigger (points of profit at which the stop migrates to entry).',
                code: 'V22A_BREAKEVEN_PRIMARY_MISSING_TRIGGER',
              });
            }
          }
          // V-22b: moveToBreakeven:true requires moveToBreakevenTrigger > 0. breakevenTrigger is IGNORED for this mode.
          // Back-compat: if the legacy `breakevenTrigger` is set (and moveToBreakevenTrigger isn't), accept it but
          // surface no warning — the field is dual-purpose under the OLD spec.
          if (d.moveToBreakeven === true && d.stopType !== 'breakeven') {
            const newField = Number(d.moveToBreakevenTrigger);
            const oldField = Number(d.breakevenTrigger);
            const hasValid = (Number.isFinite(newField) && newField > 0) || (Number.isFinite(oldField) && oldField > 0);
            if (!hasValid) {
              errors.push({
                id: 'breakeven_overlay_missing_trigger',
                type: 'error',
                message: 'V-22b: moveToBreakeven:true requires moveToBreakevenTrigger > 0.',
                blockId: block.id,
                suggestion: 'Set moveToBreakevenTrigger (points of profit at which the configured stop migrates).',
                code: 'V22B_BREAKEVEN_OVERLAY_MISSING_TRIGGER',
              });
            }
          }
        });

        // V-26b/c (TSL v1.1.1 B.6): partial/full order semantics.
        const exitBlocks = blocks.filter((b) =>
          [BlockType.EXIT, BlockType.STOP_LOSS, BlockType.TAKE_PROFIT].includes(b.type)
        );
        exitBlocks.forEach((block) => {
          const d = block.data;
          // V-26b: closeAll:true is mutually exclusive with targetEntryBlockId.
          // closeAll means "every position regardless of source Entry"; setting
          // both is semantically ambiguous.
          if (d.closeAll === true && d.targetEntryBlockId) {
            errors.push({
              id: `${block.id}-v26b-close-all-target-mutex`,
              type: 'error',
              code: 'V26B_CLOSE_ALL_TARGET_MUTUALLY_EXCLUSIVE',
              message: 'V-26b: closeAll:true and targetEntryBlockId cannot both be set on the same block.',
              blockId: block.id,
              suggestion: 'Drop one. closeAll closes every open position; targetEntryBlockId scopes the close to one Entry.',
            });
          }
          // S4b (2026-06-02) — V-26c removed (scaleOutLevels cumulative-sum
          // validator). The dead `scaleOutLevels` field is gone from the
          // BlockData type; the engine never iterated it. See audit doc.
        });

        // S4c (2026-06-02) — V-13b removed (allowModification vs
        // executionMethod compatibility). Both fields are dead — gone
        // from the BlockData type in the same slice. See audit doc.

        // S4c (2026-06-02) — V-13c removed (scaled-exit cumulative-sum
        // validator). The `scaledLevels` field is dead; engine never
        // iterated it. See audit doc.

        // S4b (2026-06-02) — V-13d removed (entry maxScaleIns realism
        // warning). The dead `allowScaling` / `maxScaleIns` fields are
        // gone from the BlockData type; the engine never iterated them.
        // See docs/BLOCK_SETTINGS_AUDIT.md S4b section.

        return errors;
      },
    });

    // V-14: Custom Block Validation (TSL V-14a through V-14e)
    this.addRule({
      id: 'custom_block_validation',
      name: 'Custom Block Validation',
      type: 'error',
      category: 'custom_block',
      validate: (blocks) => {
        const errors: ValidationError[] = [];
        const customBlocks = blocks.filter((b) => b.type === BlockType.CUSTOM);

        customBlocks.forEach((block) => {
          // Marker/AST-dispatched blocks run without host code (trusted-TS __canonical dispatch or the TSL
          // interpreter), so the host-code requirement does not apply to them.
          const cd = block.data as Record<string, unknown>;
          const dispatchesWithoutCode = !!(cd.parameters as { __canonical?: unknown } | undefined)?.__canonical || !!cd.tslAst;
          // V-14a: Custom blocks must have valid JavaScript code for their specified blockType
          if (!dispatchesWithoutCode && (!block.data.code || typeof block.data.code !== 'string')) {
            errors.push({
              id: 'custom_missing_code',
              type: 'error',
              message: 'Custom blocks must have valid JavaScript code',
              blockId: block.id,
              suggestion: 'Add JavaScript code for the custom block',
              code: 'V14A_CUSTOM_MISSING_CODE',
            });
          }

          // S4d (2026-06-02) — V-14b / V-14c / V-14d removed. The
          // `outputTypes` field they policed is gone from BlockData; the
          // engine infers subtype dispatch from `blockType` alone
          // (customBlockBacktestWorker.ts:71). Without an engine
          // consumer, linting outputTypes was dead work. See
          // docs/BLOCK_SETTINGS_AUDIT.md S4d section.

          // V-14e: Custom block parameters must have valid types and values
          if (block.data.parameters && typeof block.data.parameters !== 'object') {
            errors.push({
              id: 'custom_invalid_parameters',
              type: 'error',
              message: 'Custom block parameters must be a valid object',
              blockId: block.id,
              suggestion: 'Set parameters to a valid JavaScript object',
              code: 'V14E_CUSTOM_INVALID_PARAMETERS',
            });
          }
        });

        return errors;
      },
    });

    // Initialize warning rules
    this.initializeWarningRules();
  }

  private initializeWarningRules(): void {
    // W-1: Disconnected Blocks
    this.addRule({
      id: 'disconnected_blocks',
      name: 'Disconnected Blocks Warning',
      type: 'warning',
      category: 'best_practice',
      validate: (blocks) => {
        const errors: ValidationError[] = [];
        const reachable = new Set<string>();

        // Mark all reachable blocks from data sources
        blocks.forEach((block) => {
          if (this.isSourceBlock(block.type)) {
            this.markReachable(block.id, blocks, reachable);
          }
        });

        // Add Strategy Config as always reachable
        const configBlock = blocks.find((b) => b.type === BlockType.STRATEGY_CONFIG);
        if (configBlock) {
          reachable.add(configBlock.id);
        }

        // Check for disconnected blocks
        blocks.forEach((block) => {
          if (!reachable.has(block.id) && !isTerminalBlock(block.type)) {
            errors.push({
              id: 'disconnected_block',
              type: 'warning',
              message: `${this.getBlockTypeName(block.type)} block is not connected to any data source`,
              blockId: block.id,
              suggestion: 'Connect this block to a data source or remove it if unused',
              code: 'W1_DISCONNECTED_BLOCK',
            });
          }
        });

        return errors;
      },
    });

    // W-2: Reachability Analysis
    //
    // Only entry and exit blocks need to be reachable from a data source —
    // those fire on signal flow. stop_loss and take_profit are valid as
    // standalone terminals (price-driven: trailing, ATR, fixed-pct, RR);
    // they don't need an upstream signal. The prior implementation warned
    // on every standalone SL/TP — a false positive on canonical TSL.
    this.addRule({
      id: 'reachability_analysis',
      name: 'Reachability Analysis Warning',
      type: 'warning',
      category: 'best_practice',
      validate: (blocks) => {
        const errors: ValidationError[] = [];

        const signalDrivenTerminals = blocks.filter(
          (b) => b.type === BlockType.ENTRY || b.type === BlockType.EXIT,
        );
        signalDrivenTerminals.forEach((terminal) => {
          const hasDataSource = this.hasDataSource(terminal.id, blocks);
          if (!hasDataSource) {
            errors.push({
              id: 'orphaned_signal_chain',
              type: 'warning',
              message: `${this.getBlockTypeName(terminal.type)} block is not reachable from any data source`,
              blockId: terminal.id,
              suggestion: 'Connect this terminal block to a data source through logic blocks',
              code: 'W2_ORPHANED_SIGNAL_CHAIN',
            });
          }
        });

        return errors;
      },
    });

    // W-3: Risk Management Recommendations
    this.addRule({
      id: 'risk_management_recommendations',
      name: 'Risk Management Recommendations',
      type: 'warning',
      category: 'risk',
      validate: (blocks) => {
        const errors: ValidationError[] = [];
        const entryBlocks = blocks.filter((b) => b.type === BlockType.ENTRY);
        const stopLossBlocks = blocks.filter((b) => b.type === BlockType.STOP_LOSS);
        const takeProfitBlocks = blocks.filter((b) => b.type === BlockType.TAKE_PROFIT);

        // Check if strategy has any stop loss blocks
        if (stopLossBlocks.length === 0) {
        entryBlocks.forEach((entry) => {
            errors.push({
              id: 'entry_no_stop_loss',
              type: 'warning',
              message: 'Entry signal without accompanying Stop Loss (no risk management)',
              blockId: entry.id,
              suggestion: 'Add a Stop Loss block to manage risk',
              code: 'W3_ENTRY_NO_STOP_LOSS',
            });
          });
          }

        // Check if strategy has any take profit blocks
        if (takeProfitBlocks.length === 0) {
          entryBlocks.forEach((entry) => {
            errors.push({
              id: 'entry_no_take_profit',
              type: 'warning',
              message: 'Entry signal without accompanying Take Profit (no profit targets)',
              blockId: entry.id,
              suggestion: 'Add a Take Profit block to set profit targets',
              code: 'W3_ENTRY_NO_TAKE_PROFIT',
            });
        });
        }

        return errors;
      },
    });

    // W-4: Performance Warnings
    this.addRule({
      id: 'performance_warnings',
      name: 'Performance Warnings',
      type: 'warning',
      category: 'performance',
      validate: (blocks) => {
        const errors: ValidationError[] = [];

        // Check for excessive indicator lookback periods
        blocks.forEach((block) => {
          if (requiresStructuredFlow(block.type) && block.data.period) {
            if (block.data.period > 500) {
              errors.push({
                id: 'excessive_lookback',
                type: 'warning',
                message: `${this.getBlockTypeName(block.type)} has excessive lookback period (${block.data.period})`,
                blockId: block.id,
                suggestion: 'Consider reducing the lookback period for better performance',
                code: 'W4_EXCESSIVE_LOOKBACK',
              });
            }
          }
        });

        // Check for too many parallel indicators
        const indicatorBlocks = blocks.filter((b) => requiresStructuredFlow(b.type));
        if (indicatorBlocks.length > 20) {
          errors.push({
            id: 'too_many_indicators',
            type: 'warning',
            message: `Strategy has many indicators (${indicatorBlocks.length}), which may impact performance`,
            suggestion: 'Consider consolidating indicators or using custom blocks for complex calculations',
            code: 'W4_TOO_MANY_INDICATORS',
          });
        }

        return errors;
      },
    });

    // W-5: Expression Logic Warnings
    this.addRule({
      id: 'expression_logic_warnings',
      name: 'Expression Logic Warnings',
      type: 'warning',
      category: 'logic',
      validate: (blocks) => {
        const errors: ValidationError[] = [];
        const expressionBlocks = blocks.filter(
          (b) => b.type === BlockType.EXPRESSION
        );

        expressionBlocks.forEach((block) => {
          errors.push(...this.validateExpressionBlock(block, blocks));
        });

        return errors;
      },
    });
  }

  private addRule(rule: ValidationRule): void {
    this.rules.push(rule);
  }

  private hasValidSignalFlowToEntry(entryBlockId: string, blocks: Block[]): boolean {
    const entryBlock = blocks.find(b => b.id === entryBlockId);
    if (!entryBlock || entryBlock.type !== BlockType.ENTRY) return false;
    
    // Check if entry has input connections
    if (!entryBlock.connections?.inputs?.length) return false;
    
    // Check each input connection
    for (const inputId of entryBlock.connections.inputs) {
      const inputBlock = blocks.find(b => b.id === inputId);
      if (!inputBlock) continue;
      
      // V-3b, V-3c, V-3d: Input must be from Expression or Logic Gate
      if (![BlockType.EXPRESSION, BlockType.LOGIC_GATE].includes(inputBlock.type)) {
        return false;
      }
      
      // Recursively check if this logic block has valid signal flow from data sources
      if (!this.hasValidSignalFlowFromDataSource(inputBlock.id, blocks)) {
        return false;
      }
    }
    
    return true;
  }

  private hasValidSignalFlowFromDataSource(blockId: string, blocks: Block[]): boolean {
    const block = blocks.find(b => b.id === blockId);
    if (!block) return false;
    
    // If it's a data source block, it's valid
    if (this.isSourceBlock(block.type)) return true;
    
    // If it's a logic block, check its inputs
    if (this.isLogicBlock(block.type)) {
      if (!block.connections?.inputs?.length) return false;
      
      for (const inputId of block.connections.inputs) {
        const inputBlock = blocks.find(b => b.id === inputId);
        if (!inputBlock) continue;
        
        // Logic blocks can connect to data sources or other logic blocks
        if (this.isSourceBlock(inputBlock.type) || this.isLogicBlock(inputBlock.type)) {
          if (this.hasValidSignalFlowFromDataSource(inputBlock.id, blocks)) {
            return true;
          }
        }
      }
    }
    
    return false;
  }

  private getBlockTypeName(blockType: BlockType): string {
    const blockTypeNames: Record<BlockType, string> = {
      [BlockType.STRATEGY_CONFIG]: 'Strategy Configuration',
      [BlockType.PRICE_DATA]: 'Price Data',
      [BlockType.MOVING_AVERAGE]: 'Moving Average',
      [BlockType.MOMENTUM_OSCILLATOR]: 'Momentum Oscillator',
      [BlockType.VOLATILITY]: 'Volatility',
      [BlockType.VOLUME]: 'Volume',
      [BlockType.TREND]: 'Trend',
      [BlockType.EXPRESSION]: 'Expression',
      [BlockType.LOGIC_GATE]: 'Logic Gate',
      [BlockType.CHART_PATTERNS]: 'Chart Patterns',
      [BlockType.CANDLESTICK_PATTERNS]: 'Candlestick Patterns',
      [BlockType.TIME_FILTER]: 'Time Filter',
      [BlockType.ENTRY]: 'Entry',
      [BlockType.EXIT]: 'Exit',
      [BlockType.STOP_LOSS]: 'Stop Loss',
      [BlockType.TAKE_PROFIT]: 'Take Profit',
      [BlockType.CUSTOM]: 'Custom Block',
      [BlockType.SETUP_EVENT]: 'Setup Event',
      [BlockType.SETUP_STATE]: 'Setup State Machine',
    };
    return blockTypeNames[blockType] || 'Unknown Block';
  }

  private validateExpressionBlock(
    block: Block,
    allBlocks: Block[]
  ): ValidationError[] {
    const errors: ValidationError[] = [];
    const data = block.data;

    // Validate operator
    if (!data.operator) {
      errors.push({
        id: `${block.id}-operator-missing`,
        type: 'error',
        code: 'V6_EXPRESSION_OPERATOR_MISSING',
        message: 'Expression block must have a comparison operator',
        blockId: block.id,
        field: 'operator',
      });
    }

    // V-6f (TSL v1.1.1 A.7): range_static / range_input may ONLY pair with
    // inside_range / outside_range. Other operators reject these sources;
    // inside_range/outside_range reject scalar sources.
    const isRangeOp    = data.operator === 'inside_range' || data.operator === 'outside_range';
    const isRangeSrc   = data.rightSource === ExpressionSourceType.RANGE_STATIC
                      || data.rightSource === ExpressionSourceType.RANGE_INPUT
                      || data.rightSource === ('static_range' as ExpressionSourceType); // legacy alias
    if (isRangeOp && !isRangeSrc) {
      errors.push({
        id: `${block.id}-v6f-range-op-needs-range-src`,
        type: 'error',
        code: 'V6F_RANGE_OPERATOR_REQUIRES_RANGE_SOURCE',
        message: 'V-6f: inside_range / outside_range requires rightSource to be range_static or range_input.',
        blockId: block.id,
        field: 'rightSource',
        suggestion: 'Set rightSource to "range_static" with rightRange:{low,high} OR "range_input" with rightLowInputBlockId+rightHighInputBlockId.',
      });
    }
    if (!isRangeOp && isRangeSrc) {
      errors.push({
        id: `${block.id}-v6f-range-src-needs-range-op`,
        type: 'error',
        code: 'V6F_RANGE_SOURCE_REQUIRES_RANGE_OPERATOR',
        message: 'V-6f: range_static / range_input only valid with inside_range / outside_range.',
        blockId: block.id,
        field: 'operator',
      });
    }
    // V-6f static-range completeness check.
    if (data.rightSource === ExpressionSourceType.RANGE_STATIC || data.rightSource === ('static_range' as ExpressionSourceType)) {
      const rg = data.rightRange;
      if (!rg || typeof rg.low !== 'number' || typeof rg.high !== 'number') {
        errors.push({
          id: `${block.id}-v6f-static-range-missing`,
          type: 'error',
          code: 'V6F_RANGE_STATIC_MISSING_BOUNDS',
          message: 'V-6f: range_static requires rightRange: { low: number, high: number }.',
          blockId: block.id,
          field: 'rightRange',
        });
      } else if (rg.low > rg.high) {
        errors.push({
          id: `${block.id}-v6f-static-range-inverted`,
          type: 'error',
          code: 'V6F_RANGE_STATIC_INVERTED',
          message: `V-6f: rightRange.low (${rg.low}) must be ≤ rightRange.high (${rg.high}).`,
          blockId: block.id,
          field: 'rightRange',
        });
      }
    }
    // V-6f range_input completeness check.
    if (data.rightSource === ExpressionSourceType.RANGE_INPUT) {
      if (!data.rightLowInputBlockId || !data.rightHighInputBlockId) {
        errors.push({
          id: `${block.id}-v6f-range-input-missing`,
          type: 'error',
          code: 'V6F_RANGE_INPUT_MISSING_BLOCKS',
          message: 'V-6f: range_input requires both rightLowInputBlockId and rightHighInputBlockId.',
          blockId: block.id,
          field: 'rightLowInputBlockId',
        });
      }
    }

    // Validate left operand
    errors.push(
      ...this.validateExpressionOperand(
        block,
        'left',
        data.leftSource,
        data.leftValue,
        data.leftInputBlockId,
        data.leftPriceDataBlockId,
        data.leftOutputPort,
        data.leftPriceSource,
        allBlocks
      )
    );

    // Validate right operand
    errors.push(
      ...this.validateExpressionOperand(
        block,
        'right',
        data.rightSource,
        data.rightValue,
        data.rightInputBlockId,
        data.rightPriceDataBlockId,
        data.rightOutputPort,
        data.rightPriceSource,
        allBlocks
      )
    );

    // Validate persistence configuration
    if (data.persistence) {
      if (data.persistence.required < 1) {
        errors.push({
          id: `${block.id}-persistence-invalid`,
          type: 'error',
          code: 'V6E_EXPRESSION_PERSISTENCE_INVALID',
          message: 'Persistence must be at least 1 bar',
          blockId: block.id,
          field: 'persistence',
        });
      }

      if (data.persistence.type === 'within_period') {
        if (
          !data.persistence.period ||
          data.persistence.period < data.persistence.required
        ) {
          errors.push({
            id: `${block.id}-persistence-period-invalid`,
            type: 'error',
            code: 'V6E_EXPRESSION_PERSISTENCE_PERIOD_INVALID',
            message:
              'Within-period persistence requires a period greater than or equal to required bars',
            blockId: block.id,
            field: 'persistence',
          });
        }
      }
    }

    return errors;
  }

  private validateExpressionOperand(
    block: Block,
    side: 'left' | 'right',
    source: string | undefined,
    value: number | undefined,
    inputBlockId: string | undefined,
    priceDataBlockId: string | undefined,
    _outputPort: string | undefined,
    priceSource: string | undefined,
    allBlocks: Block[]
  ): ValidationError[] {
    const errors: ValidationError[] = [];
    const capitalizedSide = side.charAt(0).toUpperCase() + side.slice(1);

    switch (source) {
      case ExpressionSourceType.STATIC:
        if (value === undefined || value === null) {
          errors.push({
            id: `${block.id}-${side}-static-missing`,
            type: 'error',
            code: 'V6D_EXPRESSION_STATIC_VALUE_MISSING',
            message: `${capitalizedSide} operand requires a static value`,
            blockId: block.id,
            field: `${side}Value`,
          });
        }
        break;

      case ExpressionSourceType.INPUT:
        if (!inputBlockId) {
          errors.push({
            id: `${block.id}-${side}-input-missing`,
            type: 'error',
            code: 'V6A_EXPRESSION_INPUT_BLOCK_MISSING',
            message: `${capitalizedSide} operand requires a connected block selection`,
            blockId: block.id,
            field: `${side}InputBlockId`,
          });
        } else {
          const isConnected = block.connections?.inputs?.includes(inputBlockId);
          if (!isConnected) {
            errors.push({
              id: `${block.id}-${side}-input-not-connected`,
              type: 'error',
              code: 'V6A_EXPRESSION_INPUT_BLOCK_NOT_CONNECTED',
              message: `${capitalizedSide} operand references block but it's not connected`,
              blockId: block.id,
              field: `${side}InputBlockId`,
            });
          }

          const referencedBlock = allBlocks.find((b) => b.id === inputBlockId);
          if (!referencedBlock) {
            errors.push({
              id: `${block.id}-${side}-input-not-found`,
              type: 'error',
              code: 'V6A_EXPRESSION_INPUT_BLOCK_NOT_FOUND',
              message: `${capitalizedSide} operand references non-existent block`,
              blockId: block.id,
              field: `${side}InputBlockId`,
            });
          }
        }
        break;

      case ExpressionSourceType.PRICE_DATA:
        if (!priceDataBlockId) {
          errors.push({
            id: `${block.id}-${side}-price-data-missing`,
            type: 'error',
            code: 'V6B_EXPRESSION_PRICE_DATA_BLOCK_MISSING',
            message: `${capitalizedSide} operand requires a price data block selection`,
            blockId: block.id,
            field: `${side}PriceDataBlockId`,
          });
        } else {
          const priceDataBlock = allBlocks.find(
            (b) => b.id === priceDataBlockId
          );
          if (priceDataBlock && priceDataBlock.type !== BlockType.PRICE_DATA) {
            errors.push({
              id: `${block.id}-${side}-invalid-price-data-type`,
              type: 'error',
              code: 'V6B_EXPRESSION_INVALID_PRICE_DATA_TYPE',
              message: `${capitalizedSide} operand references a non-price-data block`,
              blockId: block.id,
              field: `${side}PriceDataBlockId`,
            });
          }
        }
        break;

      case ExpressionSourceType.PRICE: {
        const validPriceSources = [
          'open',
          'high',
          'low',
          'close',
          'hl2',
          'hlc3',
          'ohlc4',
        ];
        if (!priceSource || !validPriceSources.includes(priceSource)) {
          errors.push({
            id: `${block.id}-${side}-invalid-price-source`,
            type: 'error',
            code: 'V6_EXPRESSION_INVALID_PRICE_SOURCE',
            message: `${capitalizedSide} operand uses invalid price source`,
            blockId: block.id,
            field: `${side}PriceSource`,
          });
        }
        break;
      }

      default:
        if (source) {
          errors.push({
            id: `${block.id}-${side}-invalid-source`,
            type: 'error',
            code: 'V6_EXPRESSION_INVALID_SOURCE_TYPE',
            message: `${capitalizedSide} operand has invalid source type`,
            blockId: block.id,
            field: `${side}Source`,
          });
        }
    }

    return errors;
  }

  private validatePatternBlock(block: Block): ValidationError[] {
    const errors: ValidationError[] = [];

    switch (block.type) {
      case BlockType.CHART_PATTERNS:
        errors.push(...this.validateChartPatternsBlock(block));
        break;
      case BlockType.CANDLESTICK_PATTERNS:
        errors.push(...this.validateCandlestickPatternsBlock(block));
        break;
      case BlockType.CUSTOM:
        errors.push(...this.validateCustomBlock(block));
        break;
    }

    return errors;
  }

  private validateChartPatternsBlock(block: Block): ValidationError[] {
    const errors: ValidationError[] = [];

    // V-7a: Check if at least one pattern is selected
    if (!block.data.chartPatterns || block.data.chartPatterns.length === 0) {
      errors.push({
        id: `chart-patterns-${block.id}-no-patterns`,
        type: 'error',
        code: 'V7A_CHART_PATTERNS_NO_SELECTION',
        message: 'At least one chart pattern must be selected',
        blockId: block.id,
        field: 'chartPatterns',
        suggestion: 'Select one or more chart patterns to detect',
      });
    }

    // Validate sensitivity
    const validSensitivities = ['strict', 'medium', 'loose'];
    if (
      block.data.sensitivity &&
      !validSensitivities.includes(block.data.sensitivity)
    ) {
      errors.push({
        id: `chart-patterns-${block.id}-invalid-sensitivity`,
        type: 'error',
        code: 'V7_PATTERN_INVALID_SENSITIVITY',
        message: 'Invalid sensitivity setting',
        blockId: block.id,
        field: 'sensitivity',
        suggestion: 'Use strict, medium, or loose sensitivity',
      });
    }

    // Validate minimum pattern size
    if (
      block.data.minPatternSize &&
      (block.data.minPatternSize < 3 || block.data.minPatternSize > 100)
    ) {
      errors.push({
        id: `chart-patterns-${block.id}-invalid-min-size`,
        type: 'error',
        code: 'V7_PATTERN_INVALID_MIN_SIZE',
        message: 'Minimum pattern size must be between 3 and 100 bars',
        blockId: block.id,
        field: 'minPatternSize',
        suggestion: 'Set minimum pattern size between 3 and 100',
      });
    }

    // Validate confirmation bars
    if (
      block.data.confirmationBars &&
      (block.data.confirmationBars < 0 || block.data.confirmationBars > 10)
    ) {
      errors.push({
        id: `chart-patterns-${block.id}-invalid-confirmation`,
        type: 'error',
        code: 'V7_PATTERN_INVALID_CONFIRMATION',
        message: 'Confirmation bars must be between 0 and 10',
        blockId: block.id,
        field: 'confirmationBars',
        suggestion: 'Set confirmation bars between 0 and 10',
      });
    }

    return errors;
  }

  private validateCandlestickPatternsBlock(block: Block): ValidationError[] {
    const errors: ValidationError[] = [];

    // V-7b: Check if at least one pattern is selected
    if (
      !block.data.candlestickPatterns ||
      block.data.candlestickPatterns.length === 0
    ) {
      errors.push({
        id: `candlestick-patterns-${block.id}-no-patterns`,
        type: 'error',
        code: 'V7B_CANDLESTICK_PATTERNS_NO_SELECTION',
        message: 'At least one candlestick pattern must be selected',
        blockId: block.id,
        field: 'candlestickPatterns',
        suggestion: 'Select one or more candlestick patterns to detect',
      });
    }

    // Validate sensitivity
    const validSensitivities = ['strict', 'medium', 'loose'];
    if (
      block.data.sensitivity &&
      !validSensitivities.includes(block.data.sensitivity)
    ) {
      errors.push({
        id: `candlestick-patterns-${block.id}-invalid-sensitivity`,
        type: 'error',
        code: 'V7_PATTERN_INVALID_SENSITIVITY',
        message: 'Invalid sensitivity setting',
        blockId: block.id,
        field: 'sensitivity',
        suggestion: 'Use strict, medium, or loose sensitivity',
      });
    }

    // Validate confirmation bars
    if (
      block.data.confirmationBars &&
      (block.data.confirmationBars < 0 || block.data.confirmationBars > 10)
    ) {
      errors.push({
        id: `candlestick-patterns-${block.id}-invalid-confirmation`,
        type: 'error',
        code: 'V7_PATTERN_INVALID_CONFIRMATION',
        message: 'Confirmation bars must be between 0 and 10',
        blockId: block.id,
        field: 'confirmationBars',
        suggestion: 'Set confirmation bars between 0 and 10',
      });
    }

    return errors;
  }

  private validateCustomBlock(block: Block): ValidationError[] {
    // S4d (2026-06-02) — V-14b / V-14c removed from validateCustomBlock
    // (same outputTypes-policing dead code as the V-14b/c that were
    // removed from the main custom-block validation rule above). The
    // `outputTypes` field is gone from BlockData; the engine infers
    // subtype dispatch from `blockType` alone. See audit doc.
    //
    // The method is kept (no callers were removed) and returns an
    // empty array; the call sites still expect ValidationError[]
    // from `this.validateCustomBlock(block)`.
    void block;
    return [];
  }

  private calculatePerformanceMetrics(blocks: Block[]): PerformanceMetrics {
    const indicatorBlocks = blocks.filter((b) =>
      [
        BlockType.MOVING_AVERAGE,
        BlockType.MOMENTUM_OSCILLATOR,
        BlockType.VOLATILITY,
        BlockType.VOLUME,
        BlockType.TREND,
        BlockType.CUSTOM,
      ].includes(b.type)
    );

    const complexity =
      blocks.length +
      blocks.reduce(
        (sum, b) => sum + (b.connections?.outputs?.length || 0) * 0.5,
        0
      );
    const indicatorLoad = indicatorBlocks.reduce(
      (sum, b) => sum + (b.data.period || 14),
      0
    );

    return {
      complexity,
      estimatedMemoryUsage: complexity * 1024 + indicatorLoad * 512,
      estimatedExecutionTime: complexity * 2 + indicatorLoad * 0.5,
      cycleComplexity: this.calculateCyclomaticComplexity(blocks),
      indicatorLoad,
    };
  }

  private calculateCyclomaticComplexity(blocks: Block[]): number {
    let complexity = 1;

    for (const block of blocks) {
      switch (block.type) {
        case BlockType.LOGIC_GATE:
          complexity += 1;
          break;
        case BlockType.EXPRESSION:
          complexity += 1;
          break;
        case BlockType.TIME_FILTER:
          complexity += 2;
          break;
        case BlockType.CHART_PATTERNS:
        case BlockType.CANDLESTICK_PATTERNS:
        case BlockType.CUSTOM:
          complexity += 2;
          break;
        case BlockType.ENTRY:
        case BlockType.EXIT:
        case BlockType.STOP_LOSS:
        case BlockType.TAKE_PROFIT:
          complexity += block.connections?.inputs?.length || 0;
          break;
      }
    }

    return complexity;
  }

  private generateSuggestions(
    blocks: Block[],
    errors: ValidationError[],
    warnings: ValidationError[]
  ): string[] {
    const suggestions: string[] = [];

    const entryBlocks = blocks.filter((b) => b.type === BlockType.ENTRY);
    const stopLossBlocks = blocks.filter((b) => b.type === BlockType.STOP_LOSS);
    const takeProfitBlocks = blocks.filter(
      (b) => b.type === BlockType.TAKE_PROFIT
    );
    const filterBlocks = blocks.filter((b) => b.type === BlockType.TIME_FILTER);

    if (entryBlocks.length > 0 && filterBlocks.length === 0) {
      suggestions.push(
        'Consider adding time filters to improve trade quality and reduce false signals'
      );
    }

    if (stopLossBlocks.length === 0) {
      suggestions.push('Add stop loss protection to manage downside risk');
    }

    if (takeProfitBlocks.length === 0) {
      suggestions.push('Consider adding take profit targets to secure gains');
    }

    if (errors.length > 0) {
      suggestions.push('Fix critical errors before testing the strategy');
    }

    if (
      warnings.filter((w) => w.code?.includes('PATTERN_OVERFITTING')).length > 0
    ) {
      suggestions.push(
        'Consider reducing the number of patterns to prevent over-fitting'
      );
    }

    const structuralErrors = errors.filter((e) => e.code?.startsWith('V3'));
    if (structuralErrors.length > 0) {
      suggestions.push(
        'Ensure proper signal flow: indicators/patterns → logic blocks → terminal blocks'
      );
    }

    return suggestions;
  }

  validate(blocks: Block[]): ValidationResult {
    // S91 — disabled blocks are excluded from evaluation + conversion, so
    // exclude them from validation too: a disabled block's config error must
    // not fail the whole strategy. All passes below use the filtered list.
    // S92 — strip the EFFECTIVE disabled closure (explicitly-disabled blocks +
    // any condition built on them), so disabling a source block also clears the
    // now-dangling-reference errors on the expressions that read from it.
    const disabledIds = effectiveDisabledIds(blocks);
     
    blocks = blocks.filter((b) => !disabledIds.has(b.id));
    const allErrors: ValidationError[] = [];
    const allWarnings: ValidationError[] = [];

    // Run all validation rules
    for (const rule of this.rules) {
      const ruleResults = rule.validate(blocks);

      if (rule.type === 'critical' || rule.type === 'error') {
        allErrors.push(...ruleResults);
      } else {
        allWarnings.push(...ruleResults);
      }
    }

    // Calculate performance metrics
    const performance = this.calculatePerformanceMetrics(blocks);

    // Generate suggestions
    const suggestions = this.generateSuggestions(
      blocks,
      allErrors,
      allWarnings
    );

    // Convert warnings to proper ValidationWarning type
    const warnings: ValidationWarning[] = allWarnings.map((w) => ({
      id: w.id,
      type: 'best_practice',
      message: w.message,
      blockId: w.blockId,
      field: w.field,
      suggestion: w.suggestion,
      severity: w.type === 'warning' ? 'medium' : 'low',
    }));

    return {
      isValid: allErrors.length === 0,
      errors: allErrors,
      warnings,
      performance,
      suggestions,
    };
  }

  // Enhanced validation with error handling
  validateWithErrorHandling(blocks: Block[]): ValidationResult {
    try {
      if (!Array.isArray(blocks)) {
        console.warn('Invalid blocks provided to validation:', blocks);
        return {
          isValid: false,
          errors: [
            {
              id: 'invalid_blocks_array',
              type: 'critical',
              message: 'Invalid strategy data',
              code: 'INVALID_BLOCKS_ARRAY',
            },
          ],
          warnings: [],
          performance: {
            complexity: 0,
            estimatedMemoryUsage: 0,
            estimatedExecutionTime: 0,
          },
          suggestions: ['Try refreshing the page or recreating the strategy'],
        };
      }

      const validBlocks = blocks.filter(
        (block) => block && typeof block === 'object' && block.id && block.type
      );

      if (validBlocks.length !== blocks.length) {
        console.warn(
          `Filtered out ${blocks.length - validBlocks.length} invalid blocks`
        );
      }

      return this.validate(validBlocks);
    } catch (error) {
      console.error('Validation error:', error);
      return {
        isValid: false,
        errors: [
          {
            id: 'validation_system_error',
            type: 'critical',
            message: 'Validation system encountered an error',
            code: 'VALIDATION_SYSTEM_ERROR',
          },
        ],
        warnings: [],
        performance: {
          complexity: 0,
          estimatedMemoryUsage: 0,
          estimatedExecutionTime: 0,
        },
        suggestions: ['Try refreshing the page'],
      };
    }
  }

  // Utility methods for specific validations
  validateBlockConnection(
    sourceBlock: Block,
    targetBlock: Block
  ): ValidationError[] {
    const errors: ValidationError[] = [];
    const allowedTargets = TSL_CONNECTION_RULES[sourceBlock.type];

    if (!allowedTargets || !allowedTargets.includes(targetBlock.type)) {
      errors.push({
        id: 'invalid_connection',
        type: 'error',
        message: `Cannot connect ${this.getBlockTypeName(
          sourceBlock.type
        )} to ${this.getBlockTypeName(targetBlock.type)}`,
        blockId: sourceBlock.id,
        suggestion: `${this.getBlockTypeName(
          sourceBlock.type
        )} can connect to: ${
          allowedTargets?.map(this.getBlockTypeName).join(', ') || 'none'
        }`,
        code: 'INVALID_CONNECTION',
      });
    }

    return errors;
  }

  getBlockRequirements(blockType: BlockType): {
    requiredInputs: string[];
    requiredParameters: string[];
    optionalParameters: string[];
  } {
    const requirements: Record<BlockType, {
      requiredInputs: string[];
      requiredParameters: string[];
      optionalParameters: string[];
    }> = {
      [BlockType.STRATEGY_CONFIG]: {
        requiredInputs: [],
        requiredParameters: ['title', 'initial_capital'],
        optionalParameters: ['description', 'slippage', 'commission_type'],
      },
      [BlockType.PRICE_DATA]: {
        requiredInputs: [],
        requiredParameters: ['symbol', 'timeframe'],
        optionalParameters: ['dataProvider', 'adjustments'],
      },
      [BlockType.MOVING_AVERAGE]: {
        requiredInputs: [], // Implicit access to global price data in TSL v1.0
        requiredParameters: ['indicator', 'period'],
        optionalParameters: ['offset', 'smoothing'],
      },
      [BlockType.MOMENTUM_OSCILLATOR]: {
        requiredInputs: [], // Implicit access to global price data in TSL v1.0
        requiredParameters: ['indicator', 'period'],
        optionalParameters: ['smoothing', 'signal_line'],
      },
      [BlockType.VOLATILITY]: {
        requiredInputs: [], // Implicit access to global price data in TSL v1.0
        requiredParameters: ['indicator', 'period'],
        optionalParameters: ['multiplier'],
      },
      [BlockType.VOLUME]: {
        requiredInputs: [], // Implicit access to global price data in TSL v1.0
        requiredParameters: ['indicator'],
        optionalParameters: ['period', 'smoothing'],
      },
      [BlockType.TREND]: {
        requiredInputs: [], // Implicit access to global price data in TSL v1.0
        requiredParameters: ['indicator', 'period'],
        optionalParameters: ['sensitivity'],
      },
      [BlockType.EXPRESSION]: {
        requiredInputs: [],
        requiredParameters: ['operator'],
        optionalParameters: ['persistence', 'invert'],
      },
      [BlockType.LOGIC_GATE]: {
        requiredInputs: ['signal1'],
        requiredParameters: ['type'],
        optionalParameters: ['invert'],
      },
      [BlockType.CHART_PATTERNS]: {
        requiredInputs: [], // Implicit access to global price data in TSL v1.0
        requiredParameters: ['chartPatterns'],
        optionalParameters: ['sensitivity', 'confirmationBars'],
      },
      [BlockType.CANDLESTICK_PATTERNS]: {
        requiredInputs: [], // Implicit access to global price data in TSL v1.0
        requiredParameters: ['candlestickPatterns'],
        optionalParameters: ['sensitivity', 'confirmationBars'],
      },
      [BlockType.TIME_FILTER]: {
        requiredInputs: [],
        requiredParameters: ['filterType'],
        optionalParameters: ['sessions'],
      },
      [BlockType.ENTRY]: {
        requiredInputs: ['signal'],
        requiredParameters: ['entryType', 'direction'],
        optionalParameters: ['limitPrice', 'quantity_type'],
      },
      [BlockType.EXIT]: {
        requiredInputs: ['signal'],
        requiredParameters: ['exitType'],
        optionalParameters: ['limitPrice', 'quantity_type'],
      },
      [BlockType.STOP_LOSS]: {
        requiredInputs: ['entry_signal'],
        requiredParameters: ['stopType'],
        optionalParameters: ['amount', 'trailing'],
      },
      [BlockType.TAKE_PROFIT]: {
        requiredInputs: ['entry_signal'],
        requiredParameters: ['tpType'],
        optionalParameters: ['amount', 'partialFills'],
      },
      [BlockType.CUSTOM]: {
        requiredInputs: [],
        requiredParameters: ['name', 'code', 'blockType'],
        // S4d (2026-06-02): `outputTypes` removed from the optional
        // parameters list. The field is gone from BlockData.
        optionalParameters: ['parameters'],
      },
      // P9.1 stateful setup language (compiled by src/engine/setup/setupCompiler.ts)
      [BlockType.SETUP_EVENT]: {
        requiredInputs: [],
        requiredParameters: ['name'],
        optionalParameters: ['parameters'],
      },
      [BlockType.SETUP_STATE]: {
        requiredInputs: [],
        requiredParameters: ['name'],
        optionalParameters: ['parameters'],
      },
    };
    
    return requirements[blockType] || {
      requiredInputs: [],
      requiredParameters: [],
      optionalParameters: [],
    };
  }

  private hasDataSource(blockId: string, blocks: Block[], subgraph?: string[]): boolean {
    const visited = new Set<string>();
    const queue = [blockId];
    
    while (queue.length > 0) {
      const currentId = queue.shift()!;
      if (visited.has(currentId)) continue;
      visited.add(currentId);
      
      const block = blocks.find((b) => b.id === currentId);
      if (!block) continue;
      
      // Check if this is a data source
      if (this.isSourceBlock(block.type)) {
        return true;
      }
      
      // Add input blocks to queue
      if (block.connections?.inputs) {
        block.connections.inputs.forEach((inputId) => {
          if (!subgraph || subgraph.includes(inputId)) {
            queue.push(inputId);
          }
        });
      }
    }
    
    return false;
  }

  private findConnectedSubgraphs(blocks: Block[]): string[][] {
    const visited = new Set<string>();
    const subgraphs: string[][] = [];
    
    for (const block of blocks) {
      if (visited.has(block.id)) continue;
      
      const subgraph: string[] = [];
      this.dfsSubgraph(block.id, blocks, visited, subgraph);
      
      if (subgraph.length > 0) {
        subgraphs.push(subgraph);
      }
    }
    
    return subgraphs;
  }

  private dfsSubgraph(blockId: string, blocks: Block[], visited: Set<string>, subgraph: string[]): void {
    if (visited.has(blockId)) return;
    visited.add(blockId);
    subgraph.push(blockId);
    
    const block = blocks.find((b) => b.id === blockId);
    if (!block) return;
    
    // Add connected blocks
    if (block.connections?.outputs) {
      block.connections.outputs.forEach((outputId) => {
        this.dfsSubgraph(outputId, blocks, visited, subgraph);
      });
    }
    if (block.connections?.inputs) {
      block.connections.inputs.forEach((inputId) => {
        this.dfsSubgraph(inputId, blocks, visited, subgraph);
      });
    }
  }

}

// Export singleton instance
export const strategyValidator = new StrategyValidator();

// Export default class
export default StrategyValidator;
